// Codex — full-screen modal showing every role + ability. Both host and
// player can open it any time (Home, Lobby, Night, Day). Read-only.
//
// On phones it covers the whole screen so we don't fight scroll containers
// in the parent. Closes on backdrop tap or the explicit ✕ button.

import { useState } from 'react';
import { ALL_ROLES } from '../../../shared/src/roles/index.js';
import type { Faction, Lang, RoleConfig } from '../../../shared/src/types.js';
import { VERB_NAME } from '../../../shared/src/knowledge.js';
import { goalForRole } from '../../../shared/src/goals.js';
import { useFocusTrap } from '../useFocusTrap.js';
import { RoleArt } from './RoleArt.js';

interface Props {
  t: Lang;
  onClose: () => void;
  // When the player has a role, highlight it in the list.
  myRoleId?: string;
}

const FACTION_LABEL: Record<Faction, Record<Lang, string>> = {
  town: { ru: 'МИРНЫЕ', uk: 'МИРНІ' },
  mafia: { ru: 'МАФИЯ', uk: 'МАФІЯ' },
  solo: { ru: 'ОДИНОЧКИ', uk: 'ОДИНАКИ' },
};

const FACTIONS: Faction[] = ['town', 'mafia', 'solo'];

export function Codex({ t, onClose, myRoleId }: Props) {
  const [tab, setTab] = useState<Faction>(myRoleId ? roleFaction(myRoleId) : 'town');
  const [expanded, setExpanded] = useState<string | null>(myRoleId ?? null);

  const roles = ALL_ROLES.filter((r) => r.faction === tab);

  // Trap focus inside the modal, close on Escape, restore focus on unmount.
  const dialogRef = useFocusTrap<HTMLDivElement>(true, onClose);

  return (
    <div className="codex-overlay" onClick={onClose}>
      <div
        className="codex"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t === 'ru' ? 'Справочник ролей' : 'Довідник ролей'}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="codex-head">
          <div>
            <div className="sub" style={{ fontSize: 10, letterSpacing: '.32em', color: 'var(--bone-dim)' }}>
              {t === 'ru' ? 'СПРАВОЧНИК РОЛЕЙ' : 'ДОВІДНИК РОЛЕЙ'}
            </div>
            <h2 className="display" style={{ fontSize: 36, margin: 0, color: 'var(--bone)' }}>
              {t === 'ru' ? 'РОЛИ' : 'РОЛІ'} · {ALL_ROLES.length}
            </h2>
          </div>
          <button
            className="codex-close"
            onClick={onClose}
            aria-label={t === 'ru' ? 'Закрыть' : 'Закрити'}
          >
            ✕
          </button>
        </header>

        <div className="faction-tabs codex-tabs">
          {FACTIONS.map((f) => (
            <button
              key={f}
              className={`faction-tab ${tab === f ? 'on' : ''}`}
              onClick={() => setTab(f)}
            >
              <span className="sub">{FACTION_LABEL[f][t]}</span>
              <span className="mono">{ALL_ROLES.filter((r) => r.faction === f).length}</span>
            </button>
          ))}
        </div>

        <div className="codex-list">
          {roles.map((r) => (
            <CodexEntry
              key={r.id}
              role={r}
              t={t}
              isMe={r.id === myRoleId}
              expanded={expanded === r.id}
              onToggle={() => setExpanded((id) => (id === r.id ? null : r.id))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CodexEntry({
  role,
  t,
  isMe,
  expanded,
  onToggle,
}: {
  role: RoleConfig;
  t: Lang;
  isMe: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const verbList = role.abilities
    .map((a) => VERB_NAME[a.key][t] + (a.cadence === 'one-time' ? '·1x' : a.charges ? `·${a.charges}x` : ''))
    .join(' / ');

  const goal = goalForRole(role, t);

  return (
    <button className={`codex-entry ${expanded ? 'open' : ''} ${isMe ? 'mine' : ''}`} onClick={onToggle}>
      <div className="codex-entry-head">
        <div className="codex-art" style={{ borderColor: role.accent }}>
          <RoleArt id={role.id} accent={role.accent} sub={role.sub} role={role} />
        </div>
        <div className="codex-entry-text">
          <div className="display" style={{ color: role.accent, fontSize: 22, lineHeight: 1 }}>
            {role.title[t]}
            {isMe && (
              <span className="mono codex-me-tag">
                {t === 'ru' ? '· ВАША РОЛЬ' : '· ВАША РОЛЬ'}
              </span>
            )}
          </div>
          <div className="mono codex-entry-meta">
            {role.latin} · №{role.number}
            {verbList && ` · ${verbList}`}
          </div>
        </div>
        <span className="codex-chevron">{expanded ? '−' : '+'}</span>
      </div>

      {expanded && (
        <div className="codex-entry-body">
          <div
            className="codex-entry-goal"
            style={{
              color: role.accent,
              fontSize: 13,
              lineHeight: 1.4,
              marginBottom: 8,
            }}
          >
            <span className="mono" style={{ opacity: 0.75, letterSpacing: '.12em' }}>
              {goal.sideLabel}
            </span>
            {' · '}
            {goal.how}
          </div>
          <p className="codex-entry-ability">{role.ability[t]}</p>
          {role.abilities.length > 0 && (
            <div className="codex-abilities">
              {role.abilities.map((a) => (
                <div key={a.key} className="codex-ability">
                  <span className="mono codex-ability-verb" style={{ color: role.accent }}>
                    {VERB_NAME[a.key][t]}
                  </span>
                  <span className="mono codex-ability-meta">
                    {a.phase === 'night'
                      ? t === 'ru' ? 'ночь' : 'ніч'
                      : t === 'ru' ? 'день' : 'день'}
                    {' · '}
                    {a.cadence === 'one-time'
                      ? t === 'ru' ? 'один раз' : 'один раз'
                      : a.cadence === 'mandatory-night'
                        ? t === 'ru' ? 'каждую ночь (обязательно)' : 'щоночі (обов’язково)'
                        : t === 'ru' ? 'каждую ночь' : 'щоночі'}
                    {a.charges ? ` · ×${a.charges}` : ''}
                    {a.shape === 'two-target' ? ` · ${t === 'ru' ? 'две цели' : 'дві цілі'}` : ''}
                    {(a.targetless || a.shape === 'targetless') ? ` · ${t === 'ru' ? 'без цели' : 'без цілі'}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </button>
  );
}

function roleFaction(id: string): Faction {
  return ALL_ROLES.find((r) => r.id === id)?.faction ?? 'town';
}
